import { STORAGE_BUCKETS, type StorageBucket } from "@base-hormonal/contracts";
import { PublicAppError } from "./errors";

export const MAX_IMAGE_PIXELS = 8000;

export interface ImageSize {
  width: number;
  height: number;
}

/** Assinatura real do arquivo. `null` quando não reconhecemos o formato. */
export function detectMime(bytes: Uint8Array): string | null {
  const b = bytes;
  if (
    b.length >= 8 &&
    b[0] === 0x89 &&
    b[1] === 0x50 &&
    b[2] === 0x4e &&
    b[3] === 0x47 &&
    b[4] === 0x0d &&
    b[5] === 0x0a &&
    b[6] === 0x1a &&
    b[7] === 0x0a
  )
    return "image/png";
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length >= 12 && ascii(b, 0, 4) === "RIFF" && ascii(b, 8, 12) === "WEBP")
    return "image/webp";
  if (b.length >= 5 && ascii(b, 0, 5) === "%PDF-") return "application/pdf";
  return null;
}

function ascii(bytes: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...bytes.subarray(start, end));
}

/** Dimensões declaradas pelo próprio arquivo, sem decodificar a imagem. */
export function parseImageDimensions(bytes: Uint8Array, mime: string): ImageSize | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (mime === "image/png" && bytes.length >= 24) {
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (mime === "image/jpeg") {
    const sizeMarkers = new Set([
      0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
    ]);
    let offset = 2;
    while (offset + 8 < bytes.length) {
      if (bytes[offset] !== 0xff) {
        offset += 1;
        continue;
      }
      const marker = bytes[offset + 1] ?? 0;
      if (marker === 0xd8 || marker === 0xd9) {
        offset += 2;
        continue;
      }
      if (offset + 4 > bytes.length) break;
      const length = view.getUint16(offset + 2);
      if (length < 2 || offset + 2 + length > bytes.length) break;
      if (sizeMarkers.has(marker)) {
        return { width: view.getUint16(offset + 7), height: view.getUint16(offset + 5) };
      }
      offset += 2 + length;
    }
    return null;
  }
  if (mime === "image/webp" && bytes.length >= 30) {
    const chunk = ascii(bytes, 12, 16);
    if (chunk === "VP8X") {
      return {
        width: 1 + (bytes[24] ?? 0) + ((bytes[25] ?? 0) << 8) + ((bytes[26] ?? 0) << 16),
        height: 1 + (bytes[27] ?? 0) + ((bytes[28] ?? 0) << 8) + ((bytes[29] ?? 0) << 16),
      };
    }
    if (chunk === "VP8 " && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) {
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    }
  }
  return null;
}

async function decodedSize(file: File): Promise<ImageSize | null> {
  if (typeof createImageBitmap !== "function") return null;
  const bitmap = await createImageBitmap(file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
}

export interface ValidatedUpload {
  contentType: string;
  sizeBytes: number;
  dimensions: ImageSize | null;
}

/**
 * MIME declarado, assinatura, tamanho e dimensão validados antes de gastar
 * rede. O bucket e a RLS revalidam tudo; isto só antecipa a recusa e mantém a
 * mensagem pública sem nome de arquivo nem path.
 */
export async function validateUploadFile(
  bucket: StorageBucket,
  file: File,
  maxBytesOverride?: number | null,
): Promise<ValidatedUpload> {
  const policy = STORAGE_BUCKETS[bucket];
  const maxBytes = Math.min(
    policy.maxBytes,
    typeof maxBytesOverride === "number" && maxBytesOverride > 0
      ? maxBytesOverride
      : policy.maxBytes,
  );
  if (!policy.mimeTypes.includes(file.type)) {
    throw new PublicAppError("upload_type", "Formato não aceito para este tipo de arquivo.");
  }
  if (file.size <= 0) {
    throw new PublicAppError("upload_empty", "O arquivo enviado está vazio.");
  }
  if (file.size > maxBytes) {
    throw new PublicAppError(
      "upload_size",
      `O arquivo deve ter no máximo ${(maxBytes / 1024 / 1024).toFixed(2).replace(".", ",")} MB.`,
    );
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (detectMime(bytes) !== file.type) {
    throw new PublicAppError(
      "upload_signature",
      "O conteúdo do arquivo não corresponde ao formato informado.",
    );
  }
  if (file.type === "application/pdf") {
    return { contentType: file.type, sizeBytes: file.size, dimensions: null };
  }
  const declared = parseImageDimensions(bytes, file.type);
  if (
    declared === null ||
    declared.width < 1 ||
    declared.height < 1 ||
    declared.width > MAX_IMAGE_PIXELS ||
    declared.height > MAX_IMAGE_PIXELS
  ) {
    throw new PublicAppError(
      "upload_dimensions",
      `A imagem deve ter entre 1 e ${String(MAX_IMAGE_PIXELS)} pixels em cada dimensão.`,
    );
  }
  const decoded = await decodedSize(file);
  if (
    decoded !== null &&
    (decoded.width !== declared.width || decoded.height !== declared.height)
  ) {
    throw new PublicAppError(
      "upload_dimensions",
      "As dimensões declaradas e decodificadas não correspondem.",
    );
  }
  return { contentType: file.type, sizeBytes: file.size, dimensions: declared };
}
